import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useProperty,
  usePropertyField,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage, getMediaUrl, parseStorageKey, videoThumbStorageKey } from '@vibesites/ui-primitives';

const SLIDE_INTERVAL_MS = 6000;

type HeroSlideDef = SlotGroupDefinition;

const slide1Def: HeroSlideDef = {
  label: 'Slider Image 1',
  description: 'First hero slide. Clear the image to skip this slide.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const slide2Def: HeroSlideDef = {
  label: 'Slider Image 2',
  description: 'Second hero slide. Clear the image to skip this slide.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const slide3Def: HeroSlideDef = {
  label: 'Slider Image 3',
  description: 'Third hero slide. Clear the image to skip this slide.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const slide4Def: HeroSlideDef = {
  label: 'Slider Image 4',
  description: 'Fourth hero slide. Clear the image to skip this slide.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const slide5Def: HeroSlideDef = {
  label: 'Slider Image 5',
  description: 'Fifth hero slide. Clear the image to skip this slide.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const slide6Def: HeroSlideDef = {
  label: 'Slider Image 6',
  description: 'Sixth hero slide. Clear the image to skip this slide.',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

interface HeroSlideValues {
  image: string;
}

function slideLayerClass(isActive: boolean) {
  return `absolute inset-0 transition-opacity duration-700 ease-in-out ${
    isActive ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
  }`;
}

function kenBurnsStyle(reducedMotion: boolean) {
  return {
    animation: reducedMotion ? undefined : 'heroKenBurns 30s ease-in-out infinite alternate',
    willChange: reducedMotion ? undefined : 'transform',
    transformOrigin: 'bottom right',
    imageRendering: 'high-quality' as any,
  };
}

function HeroImageSlider({
  slide1,
  slide2,
  slide3,
  slide4,
  slide5,
  slide6,
  fallbackImage,
  reducedMotion,
}: {
  slide1: HeroSlideValues;
  slide2: HeroSlideValues;
  slide3: HeroSlideValues;
  slide4: HeroSlideValues;
  slide5: HeroSlideValues;
  slide6: HeroSlideValues;
  fallbackImage: string;
  reducedMotion: boolean;
}) {
  const filled = useMemo(() => {
    const items: { key: string; image: string }[] = [];
    const candidates = [
      { key: 'hero.slide_1', ...slide1 },
      { key: 'hero.slide_2', ...slide2 },
      { key: 'hero.slide_3', ...slide3 },
      { key: 'hero.slide_4', ...slide4 },
      { key: 'hero.slide_5', ...slide5 },
      { key: 'hero.slide_6', ...slide6 },
    ];
    for (const c of candidates) {
      if (c.image) items.push(c);
    }
    return items;
  }, [slide1, slide2, slide3, slide4, slide5, slide6]);

  const displaySlides = useMemo(() => {
    if (filled.length > 0) return filled;
    if (fallbackImage) return [{ key: 'fallback', image: fallbackImage }];
    return [];
  }, [filled, fallbackImage]);

  const [current, setCurrent] = useState(0);
  const count = displaySlides.length;

  const advance = useCallback(
    (dir: 1 | -1) => {
      if (count <= 1) return;
      setCurrent((prev) => (prev + dir + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1 || reducedMotion) return;
    const timer = setInterval(() => advance(1), SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [count, reducedMotion, advance]);

  useEffect(() => {
    if (count > 0 && current >= count) setCurrent(0);
  }, [count, current]);

  const usingFallback = filled.length === 0 && !!fallbackImage;

  const activeKey = displaySlides[current]?.key;
  const panStyle = kenBurnsStyle(reducedMotion);

  return (
    <div className="hero-slider-root absolute inset-0">
      {count === 0 && <div className="w-full h-full bg-[var(--vibe-color-text)]" />}

      <div className={slideLayerClass(!!slide1.image && activeKey === 'hero.slide_1')}>
        <EditableSlotGroup groupPath="hero.slide_1" definition={slide1Def} className="block h-full w-full">
          {slide1.image ? (
            <VibesitesImage
              url={slide1.image}
              alt=""
              priority={filled[0]?.key === 'hero.slide_1'}
              sizes="100vw"
              className="w-full h-full object-cover"
              style={panStyle}
            />
          ) : (
            <span className="sr-only">Slider Image 1</span>
          )}
        </EditableSlotGroup>
      </div>

      <div className={slideLayerClass(!!slide2.image && activeKey === 'hero.slide_2')}>
        <EditableSlotGroup groupPath="hero.slide_2" definition={slide2Def} className="block h-full w-full">
          {slide2.image ? (
            <VibesitesImage
              url={slide2.image}
              alt=""
              priority={filled[0]?.key === 'hero.slide_2'}
              sizes="100vw"
              className="w-full h-full object-cover"
              style={panStyle}
            />
          ) : (
            <span className="sr-only">Slider Image 2</span>
          )}
        </EditableSlotGroup>
      </div>

      <div className={slideLayerClass(!!slide3.image && activeKey === 'hero.slide_3')}>
        <EditableSlotGroup groupPath="hero.slide_3" definition={slide3Def} className="block h-full w-full">
          {slide3.image ? (
            <VibesitesImage
              url={slide3.image}
              alt=""
              priority={filled[0]?.key === 'hero.slide_3'}
              sizes="100vw"
              className="w-full h-full object-cover"
              style={panStyle}
            />
          ) : (
            <span className="sr-only">Slider Image 3</span>
          )}
        </EditableSlotGroup>
      </div>

      <div className={slideLayerClass(!!slide4.image && activeKey === 'hero.slide_4')}>
        <EditableSlotGroup groupPath="hero.slide_4" definition={slide4Def} className="block h-full w-full">
          {slide4.image ? (
            <VibesitesImage
              url={slide4.image}
              alt=""
              priority={filled[0]?.key === 'hero.slide_4'}
              sizes="100vw"
              className="w-full h-full object-cover"
              style={panStyle}
            />
          ) : (
            <span className="sr-only">Slider Image 4</span>
          )}
        </EditableSlotGroup>
      </div>

      <div className={slideLayerClass(!!slide5.image && activeKey === 'hero.slide_5')}>
        <EditableSlotGroup groupPath="hero.slide_5" definition={slide5Def} className="block h-full w-full">
          {slide5.image ? (
            <VibesitesImage
              url={slide5.image}
              alt=""
              priority={filled[0]?.key === 'hero.slide_5'}
              sizes="100vw"
              className="w-full h-full object-cover"
              style={panStyle}
            />
          ) : (
            <span className="sr-only">Slider Image 5</span>
          )}
        </EditableSlotGroup>
      </div>

      <div className={slideLayerClass(!!slide6.image && activeKey === 'hero.slide_6')}>
        <EditableSlotGroup groupPath="hero.slide_6" definition={slide6Def} className="block h-full w-full">
          {slide6.image ? (
            <VibesitesImage
              url={slide6.image}
              alt=""
              priority={filled[0]?.key === 'hero.slide_6'}
              sizes="100vw"
              className="w-full h-full object-cover"
              style={panStyle}
            />
          ) : (
            <span className="sr-only">Slider Image 6</span>
          )}
        </EditableSlotGroup>
      </div>

      {usingFallback && (
        <div className={slideLayerClass(true)}>
          <VibesitesImage
            url={fallbackImage}
            alt=""
            priority
            sizes="100vw"
            className="w-full h-full object-cover"
            style={panStyle}
          />
        </div>
      )}

      <div className="absolute inset-0 z-[1] bg-black/30 pointer-events-none" aria-hidden />
    </div>
  );
}


/**
 * Capitalize the first textual character of an HTML fragment, skipping
 * any leading tags or whitespace. Leaves the string unchanged if the
 * first non-whitespace character is a digit (per the "if not a number"
 * rule). Applied at render time so it works for both the schema default
 * and any value an author types into the inline editor.
 */
function capitalizeFirstChar(html: string): string {
  if (!html) return html;
  let inTag = false;
  for (let i = 0; i < html.length; i++) {
    const c = html[i];
    if (inTag) {
      if (c === '>') inTag = false;
      continue;
    }
    if (c === '<') {
      inTag = true;
      continue;
    }
    if (/\s/.test(c)) continue;
    // First real character — bail if it's a digit, otherwise uppercase it.
    if (/\d/.test(c)) return html;
    const upper = c.toUpperCase();
    if (upper === c) return html;
    return html.slice(0, i) + upper + html.slice(i + 1);
  }
  return html;
}

/**
 * Collapses a raw bedrooms range to its display endpoints: normalizes
 * "studio"/"std" -> "Studio", splits on any dash variant, and folds equal
 * endpoints into one (e.g. "3 - 3" -> "3", "Studio - Studio" -> "Studio").
 * Returns just the range string without any "Bedroom" suffix, so it can be
 * substituted back into a field token. Returns the original value unchanged
 * when it can't be parsed as a range.
 */
function collapseBedroomsRange(raw: string | null | undefined): string | null | undefined {
  if (!raw) return raw;
  const trimmed = String(raw).trim();
  if (!trimmed) return raw;

  // Split on any dash variant (hyphen, en-dash, em-dash) so ranges using
  // typographic dashes (e.g. "3 – 3") still collapse correctly.
  const parts = trimmed.split(/\s*[-–—]\s*/);
  const formatted = parts.map((p) => {
    const lower = p.trim().toLowerCase();
    if (lower === 'studio' || lower === 'std') return 'Studio';
    return p.trim();
  });

  // Collapse equal endpoints (e.g. "2 - 2" -> "2", "Studio - Studio" -> "Studio")
  const collapsed =
    formatted.length > 1 && formatted.every((p) => p === formatted[0])
      ? [formatted[0]]
      : formatted;

  return collapsed.join(' - ');
}

/**
 * Maps a single bedroom endpoint to its numeric value, treating
 * "Studio"/"std" as 0. Returns null when the token isn't a whole number.
 */
function bedroomToNumber(token: string): number | null {
  const lower = token.trim().toLowerCase();
  if (lower === 'studio' || lower === 'std') return 0;
  const n = Number(lower);
  return Number.isInteger(n) ? n : null;
}

/** Inverse of bedroomToNumber: 0 -> "Studio", otherwise the number itself. */
function bedroomFromNumber(n: number): string {
  return n === 0 ? 'Studio' : String(n);
}

/**
 * Expands a raw bedrooms range into an enumerated, human-readable list:
 * "studio - 2" -> "Studio, 1, and 2". Single values pass through ("1" ->
 * "1"). Falls back to the collapsed endpoint form when the range can't be
 * enumerated numerically (non-numeric endpoints, descending, etc.).
 */
function expandBedroomsRange(raw: string | null | undefined): string | null | undefined {
  const collapsed = collapseBedroomsRange(raw);
  if (!collapsed || !collapsed.trim()) return collapsed;

  const parts = collapsed.split(/\s*-\s*/);
  if (parts.length !== 2) return collapsed; // single value or unparseable

  const start = bedroomToNumber(parts[0]);
  const end = bedroomToNumber(parts[1]);
  if (start === null || end === null || start > end) return collapsed;

  const items: string[] = [];
  for (let n = start; n <= end; n++) items.push(bedroomFromNumber(n));

  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy
 * like "Studio, 1, and 2 Bedroom". Handles single values ("1" → "1
 * Bedroom"), enumerated ranges, and studios.
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const label = expandBedroomsRange(raw);
  if (!label || !label.trim()) return 'Apartments';

  return label === 'Studio' ? `${label}` : `${label} Bedroom`;
}

const bgDef: SlotGroupDefinition = {
  label: 'Background Media',
  description: 'If a video is set, it plays as the background. Otherwise the image is used.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    video: {
      label: 'Video',
      type: 'video',
      defaultField: 'property_featured_video',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bg = useSlotGroup('hero.bg_image', bgDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  const slide1 = useSlotGroup('hero.slide_1', slide1Def);
  const slide2 = useSlotGroup('hero.slide_2', slide2Def);
  const slide3 = useSlotGroup('hero.slide_3', slide3Def);
  const slide4 = useSlotGroup('hero.slide_4', slide4Def);
  const slide5 = useSlotGroup('hero.slide_5', slide5Def);
  const slide6 = useSlotGroup('hero.slide_6', slide6Def);

  const property = useProperty();

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  const showVideo = !!bg.video;
  // Derive the poster from the *bound* video so it stays correct when the
  // operator overrides the slot to a different video. Falls back to the image.
  const thumbKey = videoThumbStorageKey(parseStorageKey(bg.video || null));
  const poster =
    (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || bg.image || '';

  // Respect prefers-reduced-motion: drop the autoplay attribute (pause() alone
  // loses the race if the video hasn't buffered yet) and pause anything already
  // playing. Leaves the poster frame showing.
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) videoRef.current?.pause();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    return `<p>${beds} Apartments for Rent${location ? ` in ${location}` : ''}</p>`;
  }, [bedroomsRange, city, state]);

  // Render the subtitle through an external transform so the first character
  // is capitalized (unless it's a number) regardless of what the author types.
  // We replicate the minimal RichTextContent pipeline here (resolve field
  // tokens, strip a redundant outer <p> since EditableToken wraps in <p>)
  // and then apply capitalizeFirstChar to the resolved HTML.
  const subtitleRaw = useContentToken('hero.subtitle', { defaultValue: subtitleHtml });
  // A stored subtitle may embed the property_bedrooms_range field token, which
  // resolveRichText substitutes with the raw value ("studio - 2"). Expand the
  // range on the field value first so the resolved copy enumerates each option
  // and reads "Studio, 1, and 2".
  const resolveProperty = useMemo(() => ({
    ...(property as Record<string, any>),
    property_bedrooms_range: expandBedroomsRange(bedroomsRange as string | null),
  }), [property, bedroomsRange]);
  const subtitleResolved = useMemo(() => {
    if (!subtitleRaw) return '';
    let html = resolveRichText(subtitleRaw, resolveProperty);
    const trimmed = html.trim();
    const wrap = trimmed.match(/^<p>([\s\S]*)<\/p>$/);
    if (wrap && !/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(wrap[1])) {
      html = wrap[1];
    }
    return capitalizeFirstChar(html);
  }, [subtitleRaw, resolveProperty]);

  return (
    <section className="relative w-full h-[72vh] md:h-[82vh] min-h-[440px] max-h-[880px] overflow-hidden">
      {/* Background — video when set; otherwise slot-group image slider */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={bgDef} className="absolute inset-0">
        {showVideo ? (
          /* Decorative: the overlaid headline carries the content. */
          <>
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              src={bg.video || undefined}
              poster={poster}
              autoPlay={!reducedMotion}
              muted
              loop
              playsInline
              aria-hidden
            />
            <div className="absolute inset-0 bg-black/30 pointer-events-none" aria-hidden />
          </>
        ) : null}
        {/* Always mount slide slot groups so they stay editable when video is active. */}
        <div className={showVideo ? 'sr-only' : 'absolute inset-0'}>
          <HeroImageSlider
            slide1={{ image: slide1.image || '' }}
            slide2={{ image: slide2.image || '' }}
            slide3={{ image: slide3.image || '' }}
            slide4={{ image: slide4.image || '' }}
            slide5={{ image: slide5.image || '' }}
            slide6={{ image: slide6.image || '' }}
            fallbackImage={bg.image || ''}
            reducedMotion={reducedMotion}
          />
        </div>
      </EditableSlotGroup>

      {/* Content — nudged down from center so the subtitle/title sit lower in the hero */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8 pt-[22vh] sm:pt-[28vh] pointer-events-none">
        {/* Accent lines flanking subtitle */}
        <div className="hero-entrance hero-entrance-1 flex items-center gap-4 sm:gap-6 mb-5 max-w-5xl w-full pointer-events-auto">
          <div className="flex-1 hidden sm:flex items-center min-w-[40px]">
            <svg className="hero-accent-line w-full" style={{ height: '2px' }} viewBox="0 0 200 2" preserveAspectRatio="none">
              <line x1="200" y1="1" x2="0" y2="1" stroke="white" strokeOpacity="0.4" strokeWidth="1.5" style={{ strokeDasharray: 200, strokeDashoffset: 200, animation: 'heroLineDraw 1.4s cubic-bezier(0.22,1,0.36,1) 0.6s forwards' }} />
            </svg>
          </div>

          <EditableToken
            path="hero.subtitle"
            type="richtext"
            label="Subtitle"
            defaultValue={subtitleHtml}
            as="p"
            className="font-body text-base sm:text-xl md:text-2xl leading-relaxed opacity-90 drop-shadow-md min-w-0 text-center sm:whitespace-nowrap text-[var(--vibe-color-textOnPrimary)]"
          >
            <span
              data-vibesite-richtext=""
              dangerouslySetInnerHTML={{ __html: subtitleResolved }}
              style={{ display: 'contents' }}
            />
          </EditableToken>

          <div className="flex-1 hidden sm:flex items-center min-w-[40px]">
            <svg className="hero-accent-line w-full" style={{ height: '2px' }} viewBox="0 0 200 2" preserveAspectRatio="none">
              <line x1="0" y1="1" x2="200" y2="1" stroke="white" strokeOpacity="0.4" strokeWidth="1.5" style={{ strokeDasharray: 200, strokeDashoffset: 200, animation: 'heroLineDraw 1.4s cubic-bezier(0.22,1,0.36,1) 0.6s forwards' }} />
            </svg>
          </div>
        </div>

        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal mb-10 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg text-[var(--vibe-color-textOnPrimary)] pointer-events-auto"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef} className="pointer-events-auto">
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
            <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroKenBurns {

          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.2s; }
        .hero-entrance-2 { animation-delay: 0.45s; }
        .hero-entrance-3 { animation-delay: 0.7s; }
        .hero-cta {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 9999px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .hero-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          left: 100%;
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        @keyframes heroLineDraw {
          to { stroke-dashoffset: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          .hero-accent-line line { stroke-dashoffset: 0; animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': bgDef,
    'hero.slide_1': slide1Def,
    'hero.slide_2': slide2Def,
    'hero.slide_3': slide3Def,
    'hero.slide_4': slide4Def,
    'hero.slide_5': slide5Def,
    'hero.slide_6': slide6Def,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
